/**
 * Migration Banner Component
 *
 * Displays a prominent banner when Beta is in read-only mode,
 * directing users to Hover for continued journey work
 */

import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, ArrowRight } from 'lucide-react';

interface BetaStatus {
  isReadOnly: boolean;
  message: string;
  redirectUrl: string | null;
}

export const MigrationBanner: React.FC = () => {
  const [status, setStatus] = useState<BetaStatus | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if banner was previously dismissed this session
    const dismissed = sessionStorage.getItem('migration-banner-dismissed');
    if (dismissed) {
      setIsDismissed(true);
      return;
    }

    // Fetch Beta read-only status
    fetch('/api/beta/status')
      .then(res => res.json())
      .then(data => setStatus(data))
      .catch(err => console.error('Failed to fetch Beta status:', err));
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem('migration-banner-dismissed', 'true');
  };

  const handleGoToHover = () => {
    if (status?.redirectUrl) {
      window.location.href = status.redirectUrl;
    }
  };

  // Don't show banner if not read-only, dismissed, or status not loaded
  if (!status || !status.isReadOnly || isDismissed) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-b border-yellow-300 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-4 flex items-center justify-between gap-4">
          {/* Icon and Message */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-yellow-900">
                Beta is now read-only
              </p>
              <p className="text-sm text-yellow-800 mt-0.5">
                Your data has been migrated to Hover. All new work should be done in Hover.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={handleGoToHover}
              className="inline-flex items-center gap-2 bg-yellow-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-yellow-700 transition-colors shadow-sm"
            >
              Go to Hover
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={handleDismiss}
              className="p-2 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100 rounded-md transition-colors"
              aria-label="Dismiss banner"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MigrationBanner;
